/**
 * InstantVisualization — Phase E split (May 6, 2026).
 *
 * "Instant 3D Visualization" section of LandingPage. Renders the
 * YouTube embed when a URL is set; falls back to a poster image when
 * `youtubePosterId` is set; otherwise shows the click-to-design
 * placeholder. Behavior, classes, and click handlers are byte-identical.
 */
import { ArrowRight, Play } from 'lucide-react';
import { Button } from '@/ui/button';
import { API_URL } from '@/core/env';
import YouTubeEmbed from './YouTubeEmbed';

const InstantVisualization = ({ socialLinks, onTryItNow }) => (
  <section className="py-12 sm:py-16 px-4 sm:px-8 theme-bg-secondary border-y theme-border">
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Text Content */}
        <div className="order-2 lg:order-1">
          <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold theme-text-primary mb-6">
            Instant 3D Visualization
            <span className="block text-teal-500">For Your Closet</span>
          </h3>
          <p className="theme-text-secondary text-base sm:text-lg leading-relaxed mb-4">
            No need to wait for designs or hire professionals. Our innovative 3D design
            tool lets you create, customize, and visualize your closet in real-time.
          </p>
          <p className="text-slate-400 text-base leading-relaxed mb-6">
            Simply input your measurements, choose your style, and watch your perfect,
            tailored design come to life—directly and without surprises.
          </p>
          <Button
            onClick={onTryItNow}
            className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold px-6 py-3 rounded-lg transition-all"
            data-testid="try-it-now-btn"
          >
            Try It Now
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Video/Poster Content */}
        <div className="order-1 lg:order-2">
          <div className="aspect-video rounded-xl overflow-hidden bg-slate-800 border border-slate-700/50 shadow-2xl">
            {socialLinks.youtubeUrl ? (
              <YouTubeEmbed url={socialLinks.youtubeUrl} />
            ) : socialLinks.youtubePosterId ? (
              <div className="w-full h-full relative">
                <img
                  src={`${API_URL}/api/uploads/${socialLinks.youtubePosterId}`}
                  alt="EOC Closet Preview"
                  className="w-full h-full object-cover"
                />
                {/* No play icon - just poster image when no YouTube URL */}
              </div>
            ) : (
              <div className="w-full h-full relative group cursor-pointer bg-gradient-to-br from-slate-700 to-slate-800" onClick={onTryItNow}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-teal-500/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <Play className="h-4 w-4 sm:h-10 sm:w-10 text-teal-400 ml-1" />
                    </div>
                    <p className="text-slate-400 text-sm">Click to start designing</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default InstantVisualization;
